/** PCB incoming inspection: same SKU, split by full vs partial panels. */

export const PCB_FULL_PANEL_NOTE = "FULL PANEL";
export const PCB_PARTIAL_PANEL_NOTE = "PARTIAL PANEL";

export type PcbPanelNote = typeof PCB_FULL_PANEL_NOTE | typeof PCB_PARTIAL_PANEL_NOTE;

export type PcbPanelSplit = {
  fullPanelBoards: number;
  partialPanelBoards: number;
  partialPanelCount: number;
};

export type PcbRepackLabel = {
  quantity: number;
  panelNote: PcbPanelNote;
};

export function isPcbSku(sku: string): boolean {
  return sku.trim().toUpperCase().startsWith("PCB-");
}

export function parsePcbPanelNote(value: unknown): PcbPanelNote | null {
  if (value === PCB_FULL_PANEL_NOTE || value === PCB_PARTIAL_PANEL_NOTE) return value;
  return null;
}

export function validatePcbPanelSplit(
  receivedBoards: number,
  input: {
    fullPanelBoards: number | null;
    partialPanelBoards: number | null;
    partialPanelCount: number | null;
  },
): { ok: true; split: PcbPanelSplit } | { ok: false; message: string } {
  if (!Number.isInteger(receivedBoards) || receivedBoards <= 0) {
    return { ok: false, message: "Quantity received must be a positive whole number of boards." };
  }
  const full = input.fullPanelBoards;
  const partial = input.partialPanelBoards;
  const panels = input.partialPanelCount;
  if (full == null || partial == null) {
    return { ok: false, message: "Enter boards on full panels and boards on partial panels." };
  }
  if (!Number.isInteger(full) || full < 0 || !Number.isInteger(partial) || partial < 0) {
    return { ok: false, message: "Panel board quantities must be whole numbers." };
  }
  if (full + partial !== receivedBoards) {
    return {
      ok: false,
      message: `Boards on full panels plus boards on partial panels must equal the quantity received (${String(receivedBoards)}).`,
    };
  }
  if (partial === 0) {
    if (panels != null && panels !== 0) {
      return { ok: false, message: "Partial panel count must be 0 when there are no partial-panel boards." };
    }
    return { ok: true, split: { fullPanelBoards: full, partialPanelBoards: 0, partialPanelCount: 0 } };
  }
  if (panels == null || !Number.isInteger(panels) || panels < 1) {
    return { ok: false, message: "Enter how many partial panels those boards are on." };
  }
  return {
    ok: true,
    split: { fullPanelBoards: full, partialPanelBoards: partial, partialPanelCount: panels },
  };
}

/** One container label per repack group that has boards. */
export function pcbRepackContainerLabels(split: PcbPanelSplit): PcbRepackLabel[] {
  const labels: PcbRepackLabel[] = [];
  if (split.fullPanelBoards > 0) {
    labels.push({ quantity: split.fullPanelBoards, panelNote: PCB_FULL_PANEL_NOTE });
  }
  if (split.partialPanelBoards > 0) {
    labels.push({ quantity: split.partialPanelBoards, panelNote: PCB_PARTIAL_PANEL_NOTE });
  }
  return labels;
}

export function pcbIqcCheckInstruction(): string {
  return "Check every panel for X-out boards. Quantity received is the boards that are not X-out.";
}

export function pcbIqcRepackDirection(fullPanelBoards: number, partialPanelBoards: number): string {
  if (fullPanelBoards > 0 && partialPanelBoards > 0) {
    return "Repack so partial panels do not share a container with full panels. Saving prints a FULL PANEL label and a PARTIAL PANEL label.";
  }
  if (partialPanelBoards > 0) {
    return "All received boards are on partial panels. Saving prints one PARTIAL PANEL container label.";
  }
  if (fullPanelBoards > 0) {
    return "All received boards are on full panels. Saving prints one FULL PANEL container label.";
  }
  return "Repack so partial panels do not share a container with full panels, then print a container label for each repack container.";
}

export type LivePcbBox = {
  quantity: number;
  panelNote: string | null;
};

export function liveBoxesMatchPcbRepack(split: PcbPanelSplit, boxes: readonly LivePcbBox[]): boolean {
  const expected = pcbRepackContainerLabels(split);
  if (boxes.length !== expected.length) return false;
  const remaining = boxes.map((box) => ({ quantity: box.quantity, panelNote: box.panelNote }));
  for (const label of expected) {
    const index = remaining.findIndex(
      (box) => box.panelNote === label.panelNote && box.quantity === label.quantity,
    );
    if (index < 0) return false;
    remaining.splice(index, 1);
  }
  return remaining.length === 0;
}
